import type {
  AuthorityRecord,
  CpiSeries,
  Manifest,
  MuniSummary,
  MunicipalityTariffs,
  TariffDataSource,
  TariffPeriod,
} from "../types/tariffs";

async function fetchJson<T>(path: string): Promise<T> {
  const response = await fetch(path, { headers: { Accept: "application/json" } });
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status} ${path}`);
  }
  return (await response.json()) as T;
}

export class StaticTariffDataSource implements TariffDataSource {
  private manifestPromise?: Promise<Manifest>;
  private indexPromise?: Promise<MuniSummary[]>;

  getManifest() {
    this.manifestPromise ??= fetchJson<Manifest>("/data/manifest.json");
    return this.manifestPromise;
  }

  private async versionedPath(path: string) {
    const manifest = await this.getManifest();
    const separator = path.includes("?") ? "&" : "?";
    return `${path}${separator}dataset=${encodeURIComponent(manifest.datasetVersion)}`;
  }

  async getCpi() {
    return fetchJson<CpiSeries>(await this.versionedPath("/data/cpi.json"));
  }

  getIndex() {
    this.indexPromise ??= this.versionedPath("/data/index.json").then((path) => fetchJson<MuniSummary[]>(path));
    return this.indexPromise;
  }

  async getPeriodSnapshot(periodId: string) {
    const path = `/data/snapshots/${encodeURIComponent(periodId)}.json`;
    return fetchJson<Record<string, number>>(await this.versionedPath(path));
  }

  async getMunicipality(code: string) {
    const path = `/data/munis/${encodeURIComponent(code)}.json`;
    return fetchJson<MunicipalityTariffs>(await this.versionedPath(path));
  }

  async getAuthority(code: string) {
    const path = `/data/authorities/${encodeURIComponent(code.toUpperCase())}.json`;
    return fetchJson<AuthorityRecord>(await this.versionedPath(path));
  }

  async getPlanHistory(code: string, planId: string) {
    const path = `/data/munis/${encodeURIComponent(code)}/${encodeURIComponent(planId)}.json`;
    return fetchJson<TariffPeriod[]>(await this.versionedPath(path));
  }

  async getBoundaries() {
    type Boundaries = Awaited<ReturnType<TariffDataSource["getBoundaries"]>>;
    return fetchJson<Boundaries>(await this.versionedPath("/geo/municipalities.topojson"));
  }

  async search(query: string) {
    const normalized = query.trim().toLocaleLowerCase("en-ZA");
    if (!normalized) return [];
    const index = await this.getIndex();
    return index
      .filter((municipality) =>
        `${municipality.name} ${municipality.province} ${municipality.providers.join(" ")}`
          .toLocaleLowerCase("en-ZA")
          .includes(normalized),
      )
      .sort((a, b) => {
        const aStarts = a.name.toLocaleLowerCase("en-ZA").startsWith(normalized);
        const bStarts = b.name.toLocaleLowerCase("en-ZA").startsWith(normalized);
        return Number(bStarts) - Number(aStarts) || a.name.localeCompare(b.name);
      })
      .slice(0, 6);
  }
}

export const tariffDataSource = new StaticTariffDataSource();
